import { S3Client, PutObjectCommand, DeleteObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

// Configure S3 client with custom fetch to handle CORS
const s3Client = new S3Client({
  region: 'default', // Use 'default' or your specific region
  credentials: {
    accessKeyId: 'YJT8EFRP3DT993K7TIP0',
    secretAccessKey: 'BwwRzW7xvAkhMVfJRBu0868FXqgj86AbqO619Qex',
  },
  endpoint: 'https://teh-1.s3.poshtiban.com',
  forcePathStyle: true, // required for most S3-compatible services
  requestHandler: {
    requestTimeout: 0,
  },
});

// S3 bucket name (you may need to adjust this)
const BUCKET_NAME = 'vanaxi-chat'; // Replace with your actual bucket name

// Helper function to create thumbnail
const createThumbnail = (file: File, maxWidth: number = 500, maxHeight: number = 500): Promise<Blob> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(blob);
            } else {
              resolve(new Blob([file], { type: file.type }));
            }
          }, file.type, 0.9);
        } else {
          resolve(new Blob([file], { type: file.type }));
        }
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
};

export const s3Service = {
  // Upload file to S3 and return download URL with thumbnail
  uploadFile: async (file: File, tripId: string): Promise<{ fileUrl: string; thumbnailUrl?: string }> => {
    try {
      console.log('📤 Uploading file to S3:', file.name);

      // Generate unique file name while preserving original name
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop();
      const baseFileName = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
      const randomString = Math.random().toString(36).substring(7);
      // Save with original filename prefix + timestamp + random string
      const fileName = `${baseFileName}_${timestamp}_${randomString}.${fileExtension}`;

      // Upload original file
      const arrayBuffer = await file.arrayBuffer();
      const fileUint8Array = new Uint8Array(arrayBuffer);

      const uploadParams = {
        Bucket: BUCKET_NAME,
        Key: `${tripId}/${fileName}`,
        Body: fileUint8Array,
        ContentType: file.type,
        ACL: 'public-read' as const,
        Metadata: {
          'original-filename': file.name
        }
      };

      const command = new PutObjectCommand(uploadParams);
      await s3Client.send(command);
      
      const fileUrl = `https://teh-1.s3.poshtiban.com/${BUCKET_NAME}/${tripId}/${fileName}`;
      console.log('✅ Original file uploaded to S3 successfully:', fileUrl);
      
      // If it's an image, create and upload thumbnail
      let thumbnailUrl: string | undefined;
      if (file.type.startsWith('image/')) {
        try {
          console.log('🖼️ Creating thumbnail...');
          const thumbnailBlob = await createThumbnail(file);
          const thumbnailName = `${baseFileName}_${timestamp}_${randomString}_thumb.${fileExtension}`;
          const thumbnailBuffer = new Uint8Array(await thumbnailBlob.arrayBuffer());
          
          const thumbnailParams = {
            Bucket: BUCKET_NAME,
            Key: `${tripId}/${thumbnailName}`,
            Body: thumbnailBuffer,
            ContentType: file.type,
            ACL: 'public-read' as const,
            Metadata: {
              'original-filename': file.name,
              'thumbnail': 'true'
            }
          };
          
          const thumbnailCommand = new PutObjectCommand(thumbnailParams);
          await s3Client.send(thumbnailCommand);
          
          thumbnailUrl = `https://teh-1.s3.poshtiban.com/${BUCKET_NAME}/${tripId}/${thumbnailName}`;
          console.log('✅ Thumbnail uploaded to S3 successfully:', thumbnailUrl);
        } catch (thumbnailError) {
          console.error('⚠️ Failed to upload thumbnail:', thumbnailError);
          // Continue without thumbnail, use original file for preview
        }
      }
      
      // Return the download URL with optional thumbnail
      return { fileUrl, thumbnailUrl };
      
    } catch (error) {
      console.error('❌ Error uploading file to S3:', error);
      
      // Check if this is a CORS error
      const errorMessage = error instanceof Error ? error.message : '';
      const isCorsError = errorMessage.includes('Failed to fetch') || 
                         errorMessage.includes('CORS') ||
                         errorMessage.includes('ERR_FAILED');
      
      if (isCorsError) {
        throw new Error('خطای CORS: سرور S3 هنوز پیکربندی نشده است. لطفا فایل را با فرمت Data URL ارسال کنید یا با پشتیبانی تماس بگیرید.');
      } else if (errorMessage.includes('Network')) {
        throw new Error('خطا در اتصال به سرور. لطفا اتصال اینترنت خود را بررسی کنید.');
      } else {
        throw new Error(`خطا در آپلود فایل: ${errorMessage}`);
      }
    }
  },

  // Delete file from S3
  deleteFile: async (fileUrl: string): Promise<void> => {
    try {
      // Extract key from URL
      const url = new URL(fileUrl);
      const key = url.pathname.substring(1); // Remove leading slash

      const deleteParams = {
        Bucket: BUCKET_NAME,
        Key: key
      };

      const command = new DeleteObjectCommand(deleteParams);
      await s3Client.send(command);
      
      console.log('✅ File deleted from S3:', key);
      
    } catch (error) {
      console.error('❌ Error deleting file from S3:', error);
      throw new Error(`Failed to delete file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  },

  // Generate presigned URL for file access (if needed)
  getPresignedUrl: async (key: string, expires: number = 3600): Promise<string> => {
    try {
      const command = new GetObjectCommand({
        Bucket: BUCKET_NAME,
        Key: key
      });

      const url = await getSignedUrl(s3Client, command, { expiresIn: expires });
      return url;
      
    } catch (error) {
      console.error('❌ Error generating presigned URL:', error);
      throw new Error(`Failed to generate presigned URL: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
};